import React, { useCallback, useEffect, useState } from 'react';

import { obtenerClienteSugeridoTurno, vincularClienteTurno } from '../../services/turneroService';
import { apellidoNombre, turnoANombreDeOtraPersona } from '../../utils/nombrePersona';
import BuscadorClienteInline from './BuscadorClienteInline';

/** Cómo se resolvió el vínculo, en castellano, para explicárselo al operador.
 *  Se exporta porque la ficha de atención muestra la misma leyenda en su cabecera
 *  (VistaDetalladaAtencion), donde vive el botón que abre este componente en un modal. */
export const LEYENDA_CRITERIO = {
    CUIL: 'vinculado por CUIL',
    DNI: 'vinculado por DNI',
    TELEFONO: 'vinculado por teléfono',
    NOMBRE: 'vinculado por nombre',
    MANUAL: 'vinculado a mano',
    ALTA_EN_ATENCION: 'vinculado al registrarlo en la atención',
    TRAMITE: 'vinculado por el trámite iniciado',
};

/**
 * Vincula un turno con el cliente del estudio al que pertenece.
 *
 * POR QUÉ NO ALCANZA CON EL MATCHER AUTOMÁTICO
 * El backend vincula solo cuando no hay duda razonable. Cuando la hay —dos hermanos, un teléfono
 * familiar, un apellido demasiado corto, o el CUIL cargado contradiciendo al nombre— no adivina:
 * deja el turno suelto y devuelve candidatos. Este componente es donde esa duda se resuelve, con
 * alguien que tiene a la persona enfrente y puede preguntarle.
 *
 * Tres estados posibles:
 *   1. Ya vinculado          → mostrar quién es y ofrecer desvincular.
 *   2. Suelto, con candidatos → "¿el turno pertenece a X?" con confirmación de un click.
 *   3. Suelto, sin candidatos → buscador para elegirlo a mano.
 *
 * Desvincular no es lo mismo que "no está vinculado": queda registrado como decisión explícita
 * (DESVINCULADO_MANUAL) para que el matcher automático no lo vuelva a vincular en la próxima
 * edición del turno.
 */
export default function VincularClienteTurno({ turno, onCambio, compacto = false }) {
    const [sugerencia, setSugerencia] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    // Si el operador dice "no" a la sugerencia, se pasa al buscador manual sin volver a
    // ofrecérsela: ya contestó esa pregunta.
    const [rechazoSugerencia, setRechazoSugerencia] = useState(false);
    const [verOtros, setVerOtros] = useState(false);

    const idTurno = turno?.idTurno;
    const yaVinculado = Boolean(turno?.cuilCliente);

    // La sugerencia se pide bajo demanda y sólo cuando hace falta: no se persiste en la base
    // justamente para no tener que invalidarla cada vez que cambian los datos del turno.
    useEffect(() => {
        let vigente = true;
        if (!idTurno || yaVinculado) {
            setSugerencia(null);
            return () => { vigente = false; };
        }
        setCargando(true);
        obtenerClienteSugeridoTurno(idTurno)
            .then((data) => { if (vigente) setSugerencia(data); })
            .catch(() => { if (vigente) setSugerencia(null); })
            .finally(() => { if (vigente) setCargando(false); });
        return () => { vigente = false; };
    }, [idTurno, yaVinculado]);

    const guardarVinculo = useCallback(async (cuil) => {
        setGuardando(true);
        setError('');
        try {
            const actualizado = await vincularClienteTurno(idTurno, cuil);
            if (onCambio) onCambio(actualizado);
        } catch (e) {
            setError(e?.response?.data?.mensaje || 'No se pudo actualizar el vínculo con el cliente.');
        } finally {
            setGuardando(false);
        }
    }, [idTurno, onCambio]);

    if (!idTurno) return null;

    // ── 1. Ya vinculado ──────────────────────────────────────────────────────
    if (yaVinculado) {
        const leyenda = LEYENDA_CRITERIO[turno.vinculoClienteCriterio] || 'vinculado';
        return (
            <div className="mb-3">
                <div
                    className="d-flex align-items-center gap-2 p-2 rounded-3"
                    style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}
                >
                    <i className="bi bi-person-check-fill text-success" />
                    <div className="flex-grow-1">
                        <div className="fw-semibold">
                            {apellidoNombre(turno.clienteApellido, turno.clienteNombre)}
                        </div>
                        <small className="text-muted">
                            Cliente del estudio · {turno.cuilCliente} · {leyenda}
                        </small>
                        {turnoANombreDeOtraPersona(turno) && (
                            <small className="d-block mt-1" style={{ color: '#92400e' }}>
                                <i className="bi bi-info-circle me-1" />
                                El turno está a nombre de <strong>{turnoANombreDeOtraPersona(turno)}</strong>:
                                lo sacó otra persona para este cliente.
                            </small>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                        disabled={guardando}
                        onClick={() => guardarVinculo(null)}
                    >
                        {guardando ? 'Quitando…' : 'Desvincular'}
                    </button>
                </div>
                {error && <small className="text-danger d-block mt-1">{error}</small>}
            </div>
        );
    }

    const candidatos = sugerencia?.candidatos || [];
    const principal = candidatos[0];
    const otros = candidatos.slice(1);
    const mostrarSugerencia = !rechazoSugerencia && Boolean(principal);

    return (
        <div className="mb-3">
            {cargando && <small className="text-muted d-block mb-1">Buscando coincidencias…</small>}

            {/* ── 2. Suelto, con candidatos ────────────────────────────────── */}
            {mostrarSugerencia && (
                <div
                    className="p-2 rounded-3 mb-2"
                    style={{ background: '#fffbeb', border: '1px solid #fcd34d' }}
                >
                    <div className="d-flex align-items-start gap-2">
                        <i className="bi bi-question-circle-fill" style={{ color: '#b45309' }} />
                        <div className="flex-grow-1">
                            <div className={compacto ? 'small' : ''}>
                                ¿El turno pertenece a{' '}
                                <strong>{apellidoNombre(principal.apellido, principal.nombre)}</strong>
                                {' '}({principal.cuil})?
                            </div>
                            {sugerencia?.motivo && (
                                <small className="text-muted d-block mt-1">{sugerencia.motivo}</small>
                            )}
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success rounded-pill"
                                    disabled={guardando}
                                    onClick={() => guardarVinculo(principal.cuil)}
                                >
                                    {guardando ? 'Vinculando…' : 'Sí, vincular'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary rounded-pill"
                                    disabled={guardando}
                                    onClick={() => setRechazoSugerencia(true)}
                                >
                                    No
                                </button>
                                {otros.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link p-0 ms-1"
                                        onClick={() => setVerOtros((v) => !v)}
                                    >
                                        {verOtros ? 'Ocultar otros' : `Ver otros (${otros.length})`}
                                    </button>
                                )}
                            </div>

                            {verOtros && otros.length > 0 && (
                                <ul className="list-group mt-2">
                                    {otros.map((c) => (
                                        <li
                                            key={c.cuil}
                                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                            role="button"
                                            onClick={() => guardarVinculo(c.cuil)}
                                        >
                                            <span>
                                                {apellidoNombre(c.apellido, c.nombre)}
                                                <span className="text-muted"> · {c.cuil}</span>
                                            </span>
                                            <small className="text-muted">
                                                coincidencia {Math.round((c.score || 0) * 100)}%
                                            </small>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── 3. Suelto, sin candidatos (o el operador dijo que no) ────── */}
            {!mostrarSugerencia && !cargando && (
                <BuscadorClienteInline
                    etiqueta="Vincular con un cliente del estudio"
                    onSeleccionar={(cliente) => guardarVinculo(cliente.cuil)}
                />
            )}

            {error && <small className="text-danger d-block mt-1">{error}</small>}
        </div>
    );
}
